import { engeniApi } from '@/api/engeni.api';
import type { ApiResponseData } from '@/interfaces/api.interface';
import type {
  Project,
  ProjectBriefStatus,
  ProjectDomainIntegration,
} from '../interfaces/project.interface';
import type {
  ProjectConfigurationTask,
  ProjectConfigurationTaskStatus,
} from '../interfaces/configurationTask.interface';
import type { Domain, DomainLookup } from '../interfaces/domain.interface';
import type { ProjectMonitorStats } from '../interfaces/projectMonitorStats.interface';
import { buildV2ListParams } from '@/utils/v2List.utils';

const BASE_PATH = '/clee';

export const cleeService = {
  async getProjectsByAccountId(account_id: number) {
    const params = buildV2ListParams({
      filter: { account_id },
      pagination: { no_pagination: true },
      embed:
        'settings,landkitIntegration,googleAdsIntegration,domainIntegration.domain,latestProjectMonitor,configurationTaskSummaries.configurationTask',
    });
    return await engeniApi.get<ApiResponseData<Project[]>>(`${BASE_PATH}/projects`, { params });
  },
  async getProjectById(project_id: number) {
    return await engeniApi.get<ApiResponseData<Project>>(`${BASE_PATH}/projects/${project_id}`);
  },
  async updateProject(project_id: number, data: Partial<Project>) {
    return await engeniApi.put<ApiResponseData<Project>>(
      `${BASE_PATH}/projects/${project_id}`,
      data,
    );
  },
  async updateProjectBriefStatus(
    project_id: number,
    payload: { brief_status: ProjectBriefStatus; brief_comments: string },
  ) {
    return await engeniApi.patch<ApiResponseData<Project>>(
      `${BASE_PATH}/projects/${project_id}/brief/status`,
      payload,
    );
  },
  async getProjectConfigurationTasks(project_id: number) {
    const params = buildV2ListParams({
      pagination: { no_pagination: true },
      sort: 'configuration_task.order',
      embed: 'configurationTask',
    });

    return await engeniApi.get<ApiResponseData<ProjectConfigurationTask[]>>(
      `${BASE_PATH}/projects/${project_id}/configuration-tasks`,
      { params },
    );
  },
  async updateProjectConfigurationTaskStatus(
    project_id: number,
    project_configuration_task_id: number,
    data: { status: ProjectConfigurationTaskStatus; notes?: string },
  ) {
    return await engeniApi.patch<ApiResponseData<ProjectConfigurationTask>>(
      `${BASE_PATH}/projects/${project_id}/configuration-tasks/${project_configuration_task_id}/status`,
      data,
    );
  },
  async getDomainsByAccountId(account_id: number) {
    const params = buildV2ListParams({
      filter: { account_id },
      pagination: { no_pagination: true },
      sort: 'name',
    });
    return await engeniApi.get<ApiResponseData<Domain[]>>(`${BASE_PATH}/domains`, {
      params,
    });
  },
  async lookupDomain(domain: string) {
    return await engeniApi.post<ApiResponseData<DomainLookup>>(`${BASE_PATH}/domains/lookup`, {
      domain_name: domain,
    });
  },
  async registerDomainByProjectId(domain: string, project_id: number) {
    return await engeniApi.post<ProjectDomainIntegration>(
      `${BASE_PATH}/projects/${project_id}/integrations/domains/register`,
      {
        domain_name: domain,
      },
    );
  },
  async getProjectMonitorStats(project_id: number, filter?: Record<string, any>) {
    return await engeniApi.get<ApiResponseData<ProjectMonitorStats[]>>(
      `${BASE_PATH}/projects/${project_id}/monitors`,
      { params: filter },
    );
  },
};
